import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons'
import { Button, Col, Divider, Input, InputNumber, Row, Select, Table, Tag, Tooltip } from 'antd'
import { ColumnProps } from 'antd/lib/table'
import * as R from 'ramda'
import React, { useState } from 'react'
import { connect } from 'react-redux'
import { firestoreConnect, isLoaded, withFirebase, withFirestore } from 'react-redux-firebase'
import { compose, withHandlers, withPropsOnChange } from 'recompose'

import { CategoryId } from '../../../schemas/Category'
import { Player, TournamentPlayer } from '../../../schemas/Player'
import { GlobalTeam, Team, TeamId, TournamentTeam } from '../../../schemas/Team'
import { TenantId } from '../../../schemas/Tenant'
import { TournamentId } from '../../../schemas/Tournament'

import * as notificationActions from '../../../actions/notifications'
import { sortByTeamsAndNationalityAndLastName } from '../../../helpers/compare'
import {
  dataFromOrdered,
  findInOrdered,
  fsCollections,
  makeInBatchWithFirebase,
  saveFlights,
  saveTournamentPlayers,
} from '../../../helpers/firestore'
import { withRemovedPlayer } from '../../../helpers/flights'
import {
  addToTeam,
  asTournamentPlayer,
  getTeamsAndPositions,
  swapTeamPosition,
  withRemovedTeamMember,
} from '../../../helpers/players'
import { removeDiacritics } from '../../../helpers/text'
import { playerColumns } from '../../../shared/columns'
import { DeleteButtonWithConfirmation } from '../../ActionWithConfirmation'
import authorize from '../../higher-order/authorize'

import './TournamentPlayers.css'

type SignupPlayerProps = {
  onSignupPlayer(): void
}

const SignupPlayer = ({ onSignupPlayer }: SignupPlayerProps) => (
  <Button icon={<ArrowLeftOutlined />} shape="circle" size="small" onClick={onSignupPlayer} />
)

type ToggleDisqualifiedProps = {
  isDisqualified: boolean
  onToggleDisqualified(): void
}

const ToggleDisqualified = ({ isDisqualified, onToggleDisqualified }: ToggleDisqualifiedProps) => (
  <Tooltip title={isDisqualified ? 'Delete Disqualified' : 'Disqualify'}>
    <Button
      icon={isDisqualified ? <ExclamationCircleOutlined /> : <CheckCircleOutlined />}
      danger={isDisqualified}
      shape="circle"
      size="small"
      onClick={onToggleDisqualified}
    />
  </Tooltip>
)

type RemovePlayerProps = {
  onRemovePlayer(): void
}

const RemovePlayer: React.FunctionComponent<RemovePlayerProps> = ({ onRemovePlayer }) => (
  <DeleteButtonWithConfirmation
    title="Are you sure to remove this player from tournament?"
    popupPlacement="left"
    onConfirmAction={onRemovePlayer}
  />
)

type TeamAssignmentProps = {
  player: TournamentPlayer
  teams: Record<TeamId, Team>
  teamPositions: Record<TeamId, number[]>
} & Pick<TournamentPlayersHandlers, 'teamChange' | 'teamPositionChange'>

const TeamAssignment: React.FunctionComponent<TeamAssignmentProps> = ({
  player,
  teams,
  teamPositions,
  teamChange,
  teamPositionChange,
}: TeamAssignmentProps) => {
  const globalTeams = Object.values(teams).filter(
    (team): team is GlobalTeam => team.type === 'global',
  )
  const maxTeamNum = Object.values(teams)
    .filter((team): team is TournamentTeam => team.type === 'tournament')
    .map((team) => team.num)
    .reduce(R.max, 0)
  const tournamentTeams: TournamentTeam[] = R.range(1, maxTeamNum + 2).map((num) => ({
    type: 'tournament',
    id: String(num),
    num,
  }))

  // TODO sort
  const allTeams: Team[] = [...globalTeams, ...tournamentTeams]

  const teamOptions = allTeams.map((team) => (
    <Select.Option key={team.id} value={team.id}>
      {team.type === 'global' ? team.name : team.num}
    </Select.Option>
  ))

  const positions = player.team?.id ? teamPositions[player.team.id] : [1]

  const positionOptions = positions.map((position) => (
    <Select.Option key={position} value={position}>
      {position}
    </Select.Option>
  ))

  const onTeamChange = (selectedTeamId) => {
    const selectedTeam = teams[selectedTeamId]
    teamChange(
      player,
      selectedTeamId,
      selectedTeam?.type === 'global' ? selectedTeam.name : selectedTeamId,
    )
  }
  const onTeamPositionChange = (selectedPosition) => {
    teamPositionChange(player, selectedPosition)
  }

  return (
    <Input.Group compact>
      <Select
        allowClear
        value={R.path(['team', 'id'], player)}
        style={{ width: '9em' } /* TODO add to css */}
        size="small"
        onChange={onTeamChange}>
        {teamOptions}
      </Select>
      <Select
        value={R.path(['team', 'position'], player)}
        style={{ width: '4.5em' } /* TODO add to css */}
        size="small"
        disabled={R.isNil(player.team?.id)}
        onChange={onTeamPositionChange}>
        {positionOptions}
      </Select>
    </Input.Group>
  )
}

const tournamentPlayersColumns = (
  teams: Record<TeamId, Team>,
  teamPositions: Record<TeamId, number[]>,
  getCategoryName: TournamentPlayersInnerProps['getCategoryName'],
  removePlayer: TournamentPlayersHandlers['removePlayer'],
  teamChange: TournamentPlayersHandlers['teamChange'],
  teamPositionChange: TournamentPlayersHandlers['teamPositionChange'],
  penaltiesChange: TournamentPlayersHandlers['penaltiesChange'],
  toggleDisqualified: TournamentPlayersHandlers['toggleDisqualified'],
): Array<ColumnProps<TournamentPlayer>> => [
  playerColumns.nationality,
  playerColumns.fullName,
  {
    title: 'Categories',
    key: 'categories',
    render: (text, record) => (
      <span>
        {(record.categories || []).map((categoryId) => (
          <Tag key={categoryId}>{getCategoryName(categoryId)}</Tag>
        ))}
      </span>
    ),
  },
  {
    title: (
      <span>
        Team
        <Divider type="vertical" />
        Position
      </span>
    ),
    key: 'team',
    render: (text, record) => (
      <TeamAssignment
        player={record}
        teams={teams}
        teamPositions={teamPositions}
        teamChange={teamChange}
        teamPositionChange={teamPositionChange}
      />
    ),
  },
  {
    title: 'Penalties',
    key: 'penalties',
    width: '4em',
    render: (text, record) => {
      const onPenaltiesChange = (penalties: number) => penaltiesChange(record, penalties)

      return (
        <InputNumber
          size="small"
          min={0}
          value={record.penalties || 0}
          onChange={onPenaltiesChange}
        />
      )
    },
  },
  {
    title: 'DNF',
    key: 'disqualify',
    width: '3em',
    render: (text, record) => {
      const onToggleDisqualified = () => toggleDisqualified(record)

      return (
        <ToggleDisqualified
          isDisqualified={record.isDisqualified || false}
          onToggleDisqualified={onToggleDisqualified}
        />
      )
    },
  },
  {
    key: 'remove',
    width: '2em',
    render: (text, record) => {
      const onRemovePlayer = () => removePlayer(record)

      return <RemovePlayer onRemovePlayer={onRemovePlayer} />
    },
  },
]

const globalPlayersColumns = (
  signupPlayer: TournamentPlayersHandlers['signupPlayer'],
): Array<ColumnProps<Player>> => [
  {
    key: 'signup',
    width: '2em',
    render: (text, record) => {
      const onSignupPlayer = () => signupPlayer(record)

      return <SignupPlayer onSignupPlayer={onSignupPlayer} />
    },
  },
  playerColumns.nationality,
  playerColumns.fullName,
]

type TournamentPlayersInnerProps = {
  globalPlayers: Player[]
  tournamentPlayers: TournamentPlayer[]
  teams: Record<TeamId, Team>
  teamPositions: Record<TeamId, number[]>
  isEverythingLoaded(): boolean
  getCategoryName(categoryId: CategoryId): string | undefined
} & TournamentPlayersHandlers

const TournamentPlayers: React.FunctionComponent<TournamentPlayersInnerProps> = ({
  globalPlayers = [],
  tournamentPlayers = [],
  teams,
  teamPositions,
  isEverythingLoaded,
  getCategoryName,
  signupPlayer,
  removePlayer,
  teamChange,
  teamPositionChange,
  penaltiesChange,
  toggleDisqualified,
}: TournamentPlayersInnerProps) => {
  const [searchString, setSearch] = useState<string>('')

  const availablePlayers = globalPlayers.filter(
    (fullPlayer) =>
      removeDiacritics(`${fullPlayer.lastName} ${fullPlayer.firstName}`.toLowerCase()).includes(
        removeDiacritics(searchString.toLowerCase()),
      ) && R.none((tournamentPlayer) => fullPlayer.id === tournamentPlayer.id, tournamentPlayers),
  )

  return (
    <div className="TournamentPlayers">
      <h3>Manage players</h3>
      <Row>
        <Col span={16}>
          <div style={{ marginLeft: 10, marginRight: 5 }}>
            <Table
              dataSource={sortByTeamsAndNationalityAndLastName(tournamentPlayers)}
              columns={tournamentPlayersColumns(
                teams,
                teamPositions,
                getCategoryName,
                removePlayer,
                teamChange,
                teamPositionChange,
                penaltiesChange,
                toggleDisqualified,
              )}
              rowKey="id"
              loading={!isEverythingLoaded()}
              size="small"
              pagination={false}
            />
          </div>
        </Col>
        <Col span={8}>
          <div style={{ marginLeft: 5, marginRight: 10 }}>
            <Input.Search
              placeholder="Search"
              // tslint:disable-next-line:jsx-no-lambda
              onChange={(event) => setSearch(event.target.value)}
            />
            <Table
              dataSource={availablePlayers}
              columns={globalPlayersColumns(signupPlayer)}
              rowKey="id"
              loading={!isEverythingLoaded()}
              size="small"
              pagination={false}
            />
          </div>
        </Col>
      </Row>
    </div>
  )
}

const makePlayerSignupHandler = ({
  tournament,
  tournamentCategories,
  saveTournamentPlayersAction,
  signupPlayerFailure,
}) => (globalPlayer: Player) =>
  saveTournamentPlayersAction([
    asTournamentPlayer(tournament.dateFrom, tournamentCategories)(globalPlayer),
  ]).catch(signupPlayerFailure)

const makePlayerRemoveHandler = ({
  inBatchBound,
  tenant,
  tournamentId,
  flights,
  tournamentPlayers,
  removePlayerFailure,
}) => (removedTournamentPlayer: TournamentPlayer) =>
  inBatchBound((firestore, batch) => {
    const collection = firestore.collection(fsCollections.tournamentPlayers(tenant, tournamentId))
    batch.delete(collection.doc(removedTournamentPlayer.id))
    if (removedTournamentPlayer.team) {
      saveTournamentPlayers({ tenant, tournamentId })(firestore, batch)(
        withRemovedTeamMember(tournamentPlayers)(removedTournamentPlayer),
      )
    }
    saveFlights({ tenant, tournamentId, flights })(firestore, batch)(
      ...withRemovedPlayer(removedTournamentPlayer.id, flights),
    )
  }).catch(removePlayerFailure)

const makeTeamChangeHandler = ({
  saveTournamentPlayersAction,
  changeTeamFailure,
  tournamentPlayers,
  teamPositions,
}) => (tournamentPlayer: TournamentPlayer, selectedTeamId: TeamId, selectedTeamName?: string) => {
  // tslint:disable-next-line:no-console
  console.log(selectedTeamName)

  const modifiedTournamentPlayers = R.isNil(selectedTeamId)
    ? [
        { ...tournamentPlayer, team: {} }, // setting empty object is workaround for problem with redux-firebase refresh
        ...withRemovedTeamMember(tournamentPlayers)(tournamentPlayer),
      ]
    : [addToTeam(teamPositions)(tournamentPlayer, selectedTeamId, selectedTeamName)]

  return saveTournamentPlayersAction(modifiedTournamentPlayers).catch(changeTeamFailure)
}

const makeTeamPositionChangeHandler = ({
  saveTournamentPlayersAction,
  changeTeamFailure,
  tournamentPlayers,
}) => (tournamentPlayer: TournamentPlayer, selectedPosition: number) => {
  const counterpartPlayer = R.find(
    (otherPlayer: FIXME) =>
      Boolean(
        otherPlayer.team?.id &&
          tournamentPlayer.team?.id &&
          otherPlayer.team.id === tournamentPlayer.team.id &&
          otherPlayer.team.position === selectedPosition,
      ),
    tournamentPlayers,
  )

  return saveTournamentPlayersAction(swapTeamPosition(tournamentPlayer, counterpartPlayer)).catch(
    changeTeamFailure,
  )
}

const makePenaltiesChangeHandler = ({ saveTournamentPlayersAction }) => (
  tournamentPlayer: TournamentPlayer,
  penalties: number,
) => saveTournamentPlayersAction([{ ...tournamentPlayer, penalties }])

const makeToggleDisqualifiedHandler = ({ saveTournamentPlayersAction, setDisqualifiedFailure }) => (
  tournamentPlayer: TournamentPlayer,
) =>
  saveTournamentPlayersAction([
    {
      ...tournamentPlayer,
      isDisqualified: !tournamentPlayer.isDisqualified,
    },
  ]).catch(setDisqualifiedFailure)

type TournamentPlayersHandlers = {
  signupPlayer: ReturnType<typeof makePlayerSignupHandler>
  removePlayer: ReturnType<typeof makePlayerRemoveHandler>
  teamChange: ReturnType<typeof makeTeamChangeHandler>
  teamPositionChange: ReturnType<typeof makeTeamPositionChangeHandler>
  penaltiesChange: ReturnType<typeof makePenaltiesChangeHandler>
  toggleDisqualified: ReturnType<typeof makeToggleDisqualifiedHandler>
}

type TournamentPlayersOuterProps = {
  tenant: TenantId
  tournamentId: TournamentId
}

export default compose<TournamentPlayersInnerProps, TournamentPlayersOuterProps>(
  authorize('admin'),
  withFirebase,
  withFirestore,
  firestoreConnect<FIXME>(({ tenant, tournamentId }) => [
    { collection: fsCollections.tournaments(tenant), doc: tournamentId },
    {
      collection: fsCollections.globalPlayers(tenant),
      orderBy: [['nationality'], ['lastName'], ['firstName']] as FIXME,
    },
    { collection: fsCollections.tournamentCategories(tenant, tournamentId) },
    { collection: fsCollections.tournamentPlayers(tenant, tournamentId) },
    { collection: fsCollections.tournamentFlights(tenant, tournamentId) },
    { collection: fsCollections.globalTeams(tenant) },
  ]),
  connect(({ firestore }: FIXME, { tenant, tournamentId }: FIXME) => ({
    globalPlayers: firestore.ordered[fsCollections.globalPlayers(tenant)],
    tournament: findInOrdered(firestore.ordered[fsCollections.tournaments(tenant)], tournamentId),
    tournamentPlayers: firestore.ordered[fsCollections.tournamentPlayers(tenant, tournamentId)],
    tournamentCategories:
      firestore.ordered[fsCollections.tournamentCategories(tenant, tournamentId)],
    tournamentCategoriesMap: dataFromOrdered(
      firestore.ordered[fsCollections.tournamentCategories(tenant, tournamentId)],
    ),
    flights: firestore.ordered[fsCollections.tournamentFlights(tenant, tournamentId)],
    globalTeams: firestore.ordered[fsCollections.globalTeams(tenant)], // TODO for isLoaded only
    globalTeamsMap: dataFromOrdered(firestore.ordered[fsCollections.globalTeams(tenant)]),
  })),
  withPropsOnChange(['tournamentPlayers'], ({ tournamentPlayers = [], globalTeamsMap = {} }) =>
    getTeamsAndPositions(globalTeamsMap, tournamentPlayers),
  ),
  withHandlers({
    inBatchBound: makeInBatchWithFirebase,
  }),
  withHandlers({
    isEverythingLoaded: ({
      globalPlayers,
      tournament,
      tournamentPlayers,
      tournamentCategories,
      tournamentCategoriesMap,
      flights,
      globalTeams,
    }) => () =>
      isLoaded(
        globalPlayers,
        tournament,
        tournamentPlayers,
        tournamentCategories,
        tournamentCategoriesMap,
        flights,
        globalTeams,
      ),
    signupPlayerFailure: ({ dispatch }) => (err) => {
      // tslint:disable-next-line:no-console
      console.error(err)
      dispatch(notificationActions.notifyFailure('Failed to signup player'))
    },
    removePlayerFailure: ({ dispatch }) => (err) => {
      // tslint:disable-next-line:no-console
      console.error(err)
      dispatch(notificationActions.notifyFailure('Failed to remove player from tournament'))
    },
    changeTeamFailure: ({ dispatch }) => (err) => {
      // tslint:disable-next-line:no-console
      console.error(err)
      dispatch(notificationActions.notifyFailure("Failed to change player's team"))
    },
    setDisqualifiedFailure: ({ dispatch }) => (err) => {
      // tslint:disable-next-line:no-console
      console.error(err)
      dispatch(notificationActions.notifyFailure('Failed to set disqualification'))
    },
    getCategoryName: ({ tournamentCategoriesMap }) => (categoryId) =>
      R.path([categoryId, 'name:en'], tournamentCategoriesMap), // TODO language selection
    saveTournamentPlayersAction: ({ inBatchBound, tenant, tournamentId }) => (tournamentPlayers) =>
      inBatchBound((firestore, batch) =>
        saveTournamentPlayers({ tenant, tournamentId })(firestore, batch)(tournamentPlayers),
      ),
  }),
  withHandlers<any /* TODO */, TournamentPlayersHandlers>({
    signupPlayer: makePlayerSignupHandler,
    removePlayer: makePlayerRemoveHandler,
    teamChange: makeTeamChangeHandler,
    teamPositionChange: makeTeamPositionChangeHandler,
    penaltiesChange: makePenaltiesChangeHandler,
    toggleDisqualified: makeToggleDisqualifiedHandler,
  }),
)(TournamentPlayers)



// WEBPACK FOOTER //
// ./src/components/tournament/management/TournamentPlayers.tsx